import { Injectable } from '@angular/core';

const REFRESH_TOKEN_STORAGE_KEY = 'liImportRefreshToken';
const REPLY_TOKEN_STORAGE_KEY = 'liImportReplyToken';

/**
 * Holds the BL-408 import bearer token in memory only (never a cookie, never localStorage) and
 * the refresh token in browser storage -- that's the piece that survives a page reload or a
 * closed tab and makes silent return-to-upload possible while LinkedIn prepares the export. The
 * backend still enforces the refresh token's short absolute lifetime; the bearer token itself is
 * intentionally never persisted anywhere, and a reload always re-mints it via a refresh call.
 */
@Injectable({ providedIn: 'root' })
export class LinkedInImportTokenService {
    private bearerToken: string | null = null;

    getBearerToken(): string | null {
        return this.bearerToken;
    }

    setBearerToken(token: string): void {
        this.bearerToken = token;
    }

    clearBearerToken(): void {
        this.bearerToken = null;
    }

    getStoredRefreshToken(): string | null {
        return this.getSessionItem(REFRESH_TOKEN_STORAGE_KEY) ?? this.getLocalItem(REFRESH_TOKEN_STORAGE_KEY);
    }

    setStoredRefreshToken(token: string): void {
        this.setSessionItem(REFRESH_TOKEN_STORAGE_KEY, token);
        this.setLocalItem(REFRESH_TOKEN_STORAGE_KEY, token);
    }

    clearStoredRefreshToken(): void {
        this.removeSessionItem(REFRESH_TOKEN_STORAGE_KEY);
        this.removeLocalItem(REFRESH_TOKEN_STORAGE_KEY);
    }

    /**
     * BL-415: the reply token authenticates resend-setup-link once the bearer/refresh token are
     * both dead -- it's what a stale magic-link failure redirect carries. Keep it session-scoped;
     * unlike the short-lived refresh token, this credential exists only for the manual resend
     * recovery path.
     */
    getStoredReplyToken(): string | null {
        return this.getSessionItem(REPLY_TOKEN_STORAGE_KEY);
    }

    setStoredReplyToken(token: string): void {
        this.setSessionItem(REPLY_TOKEN_STORAGE_KEY, token);
    }

    clearStoredReplyToken(): void {
        this.removeSessionItem(REPLY_TOKEN_STORAGE_KEY);
    }

    private getSessionItem(key: string): string | null {
        try {
            return sessionStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private setSessionItem(key: string, value: string): void {
        try {
            sessionStorage.setItem(key, value);
        } catch {
            // Private-browsing / storage-disabled contexts: reload recovery simply won't be
            // available for this session, which is a degraded experience, not a broken one.
        }
    }

    private removeSessionItem(key: string): void {
        try {
            sessionStorage.removeItem(key);
        } catch {
            // See setSessionItem.
        }
    }

    private getLocalItem(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private setLocalItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch {
            // See setSessionItem.
        }
    }

    private removeLocalItem(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch {
            // See setSessionItem.
        }
    }
}
